import { loadStripe } from "@stripe/stripe-js";

/**
 * Loads Stripe.js once per publishable key.
 *
 * The key comes from REACT_APP_STRIPE_PUBLISHABLE_KEY (set in .env.development.local or CI);
 * when it is empty, the key returned by the backend (cards/setup-intent -> publishableKey) is used.
 * Never hard-code keys here.
 */
const stripePromises = {};

export const getStripePublishableKey = (fallbackKey) =>
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || fallbackKey || "";

export const getStripe = (fallbackKey) => {
  const key = getStripePublishableKey(fallbackKey);
  if (!key) {
    return null;
  }
  if (!stripePromises[key]) {
    stripePromises[key] = loadStripe(key);
  }
  return stripePromises[key];
};

/** Stripe card brand -> label used by the card screens (Stripe returns e.g. "amex"). */
export const cardBrandLabel = (brand) => {
  switch ((brand || "").toLowerCase()) {
    case "visa":
      return "Visa";
    case "mastercard":
      return "Mastercard";
    case "amex":
    case "american express":
      return "American Express";
    case "discover":
      return "Discover";
    case "diners":
      return "Diners Club";
    case "jcb":
      return "JCB";
    case "unionpay":
      return "UnionPay";
    case "unknown":
      return "Card";
    default:
      return brand || "Card";
  }
};

/**
 * Formats a billing date-time (ISO-8601 UTC instant from the backend, e.g. "2026-10-31T18:00:00Z")
 * as a calendar date in the company's billing timezone, using the user's date format setting
 * (same formats as formatDateWithContext). Falls back to the browser timezone.
 */
export const formatBillingDate = (value, timeZone) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
  } catch (e) {
    // Unknown timezone id: use the browser timezone.
    parts = new Intl.DateTimeFormat("en-US", { year: "2-digit", month: "2-digit", day: "2-digit" }).formatToParts(d);
  }
  const part = (type) => (parts.find((p) => p.type === type) || {}).value || "";
  const day = part("day");
  const month = part("month");
  const year = part("year");
  let format = "MM/DD/YY";
  try {
    format = localStorage.getItem("dateFormat") || "MM/DD/YY";
  } catch (e) {
    // storage unavailable: default format
  }
  switch (format) {
    case "DD/MM/YY":
      return `${day}/${month}/${year}`;
    case "YY/MM/DD":
      return `${year}/${month}/${day}`;
    default:
      return `${month}/${day}/${year}`;
  }
};
